import type { Timezone } from '@ipfabric/utils/timezones';
import { optionStateToFormData } from './cronState';
import type {
  CronEveryOption,
  CronFormData,
  CronOptionStates,
  CustomOptionState,
  DayOptionState,
  HourOptionState,
  MonthOptionState,
  WeekOptionState,
  YearOptionState,
} from './cronTypes';
import {
  cronWildcard,
  isNumberOrCommaSeparatedListWithinRangePublic,
  isNumberWithinRange,
  isWildcardOrNumberOrCommaSeparatedListWithinRange,
  isWildcardValue,
} from './cronValidation';

/**
 * Converts a comma-separated string or array to an array of strings
 * Handles empty strings and wildcards by returning an empty array
 */
function parseCommaSeparatedToArray(
  value: string | string[] | undefined,
  wildcard: string,
): string[] {
  if (Array.isArray(value)) {
    return value.filter((item) => item.trim() !== '' && item !== wildcard);
  }

  if (value === undefined || value === '' || value === wildcard) {
    return [];
  }

  if (!value.includes(',')) {
    const trimmed = value.trim();
    return trimmed === '' || trimmed === wildcard ? [] : [trimmed];
  }

  return value
    .split(',')
    .filter((token) => token.trim() !== '' && token !== wildcard);
}

/**
 * Parse cron expression parts into option state
 * Helper function to split and normalize cron expression parts
 */
function parseCronParts(expression: string): {
  minute: string;
  hour: string;
  day: string;
  month: string;
  weekDay: string;
} {
  const parts = expression.trim().split(/\s+/);
  const [
    minute = '',
    hour = cronWildcard,
    day = cronWildcard,
    month = cronWildcard,
    weekDay = cronWildcard,
  ] = parts;

  return { minute, hour, day, month, weekDay };
}

/**
 * Detect which option type a cron expression matches based on its structure
 *
 * Detection priority:
 * 1. Year: non-wildcard months and days
 * 2. Month: wildcard months, non-wildcard days
 * 3. Week: wildcard months/days, non-wildcard week days
 * 4. Day: all wildcards except hour/minute
 * 5. Hour: all wildcards except minute
 * 6. Custom: everything else
 */
function detectCronOption(
  minute: string,
  hour: string,
  day: string,
  month: string,
  weekDay: string,
): CronEveryOption {
  const minuteIsValidNumber = isNumberWithinRange(minute, 0, 59);
  const hourIsValidNumber = isNumberWithinRange(hour, 0, 23);
  const hourIsWildcard = isWildcardValue(hour);
  const monthMatchesYear = isWildcardOrNumberOrCommaSeparatedListWithinRange(
    month,
    1,
    12,
  );
  const dayMatchesYear = isWildcardOrNumberOrCommaSeparatedListWithinRange(
    day,
    1,
    31,
  );
  const dayIsNumberList = isNumberOrCommaSeparatedListWithinRangePublic(
    day,
    1,
    31,
  );
  const weekDayIsNumberList = isNumberOrCommaSeparatedListWithinRangePublic(
    weekDay,
    0,
    6,
  );
  const monthIsWildcard = isWildcardValue(month);
  const dayIsWildcard = isWildcardValue(day);
  const weekDayIsWildcard = isWildcardValue(weekDay);

  if (
    minuteIsValidNumber &&
    hourIsValidNumber &&
    monthMatchesYear &&
    dayMatchesYear &&
    !monthIsWildcard
  ) {
    return 'year';
  }

  if (
    minuteIsValidNumber &&
    hourIsValidNumber &&
    monthIsWildcard &&
    weekDayIsWildcard &&
    dayIsNumberList
  ) {
    return 'month';
  }

  if (
    minuteIsValidNumber &&
    hourIsValidNumber &&
    monthIsWildcard &&
    dayIsWildcard &&
    weekDayIsNumberList
  ) {
    return 'week';
  }

  if (
    minuteIsValidNumber &&
    hourIsValidNumber &&
    monthIsWildcard &&
    dayIsWildcard &&
    weekDayIsWildcard
  ) {
    return 'day';
  }

  if (
    minuteIsValidNumber &&
    hourIsWildcard &&
    monthIsWildcard &&
    dayIsWildcard &&
    weekDayIsWildcard
  ) {
    return 'hour';
  }

  return 'custom';
}

/**
 * Parse cron expression parts into YearOptionState
 */
function parseYearState(
  minute: string,
  hour: string,
  day: string,
  month: string,
): YearOptionState {
  const months = parseCommaSeparatedToArray(month, cronWildcard);
  const days = parseCommaSeparatedToArray(day, cronWildcard);

  return {
    months,
    days,
    hour: hour || '',
    minute: minute || '',
  };
}

/**
 * Parse cron expression parts into MonthOptionState
 */
function parseMonthState(
  minute: string,
  hour: string,
  day: string,
): MonthOptionState {
  const days = parseCommaSeparatedToArray(day, cronWildcard);

  return {
    days,
    hour: hour || '',
    minute: minute || '',
  };
}

/**
 * Parse cron expression parts into WeekOptionState
 */
function parseWeekState(
  minute: string,
  hour: string,
  weekDay: string,
): WeekOptionState {
  const weekDays = parseCommaSeparatedToArray(weekDay, cronWildcard);

  return {
    weekDays,
    hour: hour || '',
    minute: minute || '',
  };
}

/**
 * Parse cron expression parts into DayOptionState
 */
function parseDayState(minute: string, hour: string): DayOptionState {
  return {
    hour: hour || '',
    minute: minute || '',
  };
}

/**
 * Parse cron expression parts into HourOptionState
 */
function parseHourState(minute: string): HourOptionState {
  return {
    minute: minute || '',
  };
}

/**
 * Parse cron expression parts into CustomOptionState
 */
function parseCustomState(
  minute: string,
  hour: string,
  day: string,
  month: string,
  weekDay: string,
): CustomOptionState {
  return {
    minute: minute || '',
    hour: hour || cronWildcard,
    days: day || cronWildcard,
    months: month || cronWildcard,
    weekDays: weekDay || cronWildcard,
  };
}

/**
 * Parse cron expression to form data
 * Cron format: minute hour day month dayOfWeek
 * Example: "30 14 1,15 1,6,12 *" = 14:30 on 1st and 15th of Jan, Jun, Dec
 *
 * This function parses the expression directly into the appropriate option state,
 * then converts it to CronFormData for backward compatibility.
 */
export function parseCronExpression(
  expression: string,
  timezone: Timezone,
  preferredEvery?: CronEveryOption,
): CronFormData {
  const { minute, hour, day, month, weekDay } = parseCronParts(expression);

  // Normalize comma-separated values to handle edge cases:
  // - Trailing commas (e.g., "1,15," -> "1,15")
  // - Whitespace around commas (e.g., "1, 15" -> "1,15")
  // Wildcards are already handled by isWildcardValue() checks, but filtering them here
  // ensures they don't appear in comma-separated lists
  const normalizedDay = isWildcardValue(day)
    ? cronWildcard
    : parseCommaSeparatedToArray(day, cronWildcard).join(',');
  const normalizedMonth = isWildcardValue(month)
    ? cronWildcard
    : parseCommaSeparatedToArray(month, cronWildcard).join(',');
  const normalizedWeekDay = isWildcardValue(weekDay)
    ? cronWildcard
    : parseCommaSeparatedToArray(weekDay, cronWildcard).join(',');

  const detectedOption =
    preferredEvery ??
    detectCronOption(
      minute,
      hour,
      normalizedDay,
      normalizedMonth,
      normalizedWeekDay,
    );

  // Parse directly into the appropriate option state
  let optionState: CronOptionStates[CronEveryOption];
  switch (detectedOption) {
    case 'year':
      optionState = parseYearState(minute, hour, day, month);
      break;
    case 'month':
      optionState = parseMonthState(minute, hour, day);
      break;
    case 'week':
      optionState = parseWeekState(minute, hour, weekDay);
      break;
    case 'day':
      optionState = parseDayState(minute, hour);
      break;
    case 'hour':
      optionState = parseHourState(minute);
      break;
    case 'custom':
      optionState = parseCustomState(minute, hour, day, month, weekDay);
      break;
  }

  // Convert option state back to CronFormData for backward compatibility
  return optionStateToFormData(optionState, detectedOption, timezone);
}
